import { concat } from '@ember/helper';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

interface PrismBulletGraphSignature {
  Args: {
    comparison?: number;
    // `current` is null when data is missing
    current?: number | null;
    invertDifferenceColor?: boolean;
    label: string;
    mark?: number;
    // `max` represents the highest value that a group of graphs can have
    max?: number;
    symbol: string;
  };
}

export default class PrismBulletGraph extends Component<PrismBulletGraphSignature> {
  get classForDifference(): string {
    const { invertDifferenceColor } = this.args;
    const { difference } = this;

    if (difference === 0) {
      return '';
    }

    if (invertDifferenceColor) {
      return difference > 0
        ? 'difference-value--negative'
        : 'difference-value--positive';
    }

    return difference > 0
      ? 'difference-value--positive'
      : 'difference-value--negative';
  }

  get comparisonValue(): number {
    return this.args.comparison ?? 0;
  }

  get currentValue(): number {
    return this.args.current ?? 0;
  }

  get difference(): number {
    if (this.comparisonValue === 0) {
      return 0;
    }

    return this.currentValue - this.comparisonValue;
  }

  get formattedDifference(): string {
    const { symbol } = this.args;
    const { difference } = this;

    if (difference === 0) {
      return '';
    }

    return difference > 0
      ? `+${difference}${symbol}`
      : `${difference}${symbol}`;
  }

  get isDataMissing(): boolean {
    return this.args.current === null;
  }

  get markValue(): number {
    return this.args.mark ?? 0;
  }

  @cached get maxValue(): number {
    const { max } = this.args;

    const maximumValue = Math.max(
      this.comparisonValue,
      this.currentValue,
      this.markValue,
    );

    if (max === undefined) {
      return maximumValue;
    }

    return Math.max(max, maximumValue);
  }

  get styleForComparisonValue(): string {
    const comparisonProportion = this.comparisonValue / this.maxValue;

    return `width: ${100 * comparisonProportion}%;`;
  }

  get styleForCurrentValue(): string {
    const currentProportion = this.currentValue / this.maxValue;

    return currentProportion
      ? `width: ${100 * currentProportion}%;`
      : 'width: 5px';
  }

  get styleForMarkValue(): string {
    const markProportion = this.markValue / this.maxValue;

    return `width: ${100 * markProportion}%;`;
  }

  <template>
    <div class="prism-bullet-graph">
      <p class="text-200--regular">
        {{@label}}
      </p>

      <div class="prism-bullet-graph__content">
        {{#if this.isDataMissing}}
          <div class="bars"></div>
        {{else}}
          <div class="bars">
            {{#if this.comparisonValue}}
              <div
                class="comparison-bar"
                style={{this.styleForComparisonValue}}
              ></div>
            {{/if}}

            <div class="current-bar" style={{this.styleForCurrentValue}}></div>

            {{#if this.markValue}}
              <div class="mark-line" style={{this.styleForMarkValue}}></div>
            {{/if}}
          </div>

          <p class="current-value text-200--regular">
            {{concat this.currentValue @symbol}}
          </p>

          {{#if this.difference}}
            <p
              class={{concat
                "difference-value"
                " "
                this.classForDifference
                " "
                "text-200--regular"
              }}
            >
              {{this.formattedDifference}}
            </p>
          {{/if}}
        {{/if}}
      </div>
    </div>
  </template>
}
